// Comprehensive Caching Service for Agricverse PWA
import localforage from 'localforage';

class CacheService {
  constructor() {
    // Configure storage instances
    this.apiCache = localforage.createInstance({
      name: 'AgricverseApp',
      storeName: 'apiCache'
    });

    this.offlineQueue = localforage.createInstance({
      name: 'AgricverseApp',
      storeName: 'offlineQueue'
    });

    this.metadataStore = localforage.createInstance({
      name: 'AgricverseApp',
      storeName: 'metadata'
    });

    // Enhanced cache expiration times for offline-first experience (in milliseconds)
    this.cacheExpirations = {
      // Critical data - longer expiration for better offline experience
      default: 15 * 60 * 1000, // 15 minutes (increased from 5)
      produce: 30 * 60 * 1000, // 30 minutes (increased from 10)
      farmManagement: 45 * 60 * 1000, // 45 minutes (increased from 15)
      profile: 2 * 60 * 60 * 1000, // 2 hours (increased from 30 min)
      staticData: 24 * 60 * 60 * 1000, // 24 hours (increased from 1 hour)
      categories: 24 * 60 * 60 * 1000, // 24 hours - rarely changes

      // Dynamic data - moderate expiration
      orders: 10 * 60 * 1000, // 10 minutes (increased from 2)
      wallet: 5 * 60 * 1000, // 5 minutes (increased from 2)
      withdrawals: 15 * 60 * 1000, // 15 minutes (increased from 5)
      products: 20 * 60 * 1000, // 20 minutes for marketplace products

      // Real-time data - shorter expiration
      conversations: 3 * 60 * 1000, // 3 minutes (increased from 1)
      messages: 1 * 60 * 1000, // 1 minute for chat messages

      // Emergency fallback - very long expiration for critical offline access
      emergency: 7 * 24 * 60 * 60 * 1000, // 7 days for emergency offline access
    };

    // Initialize sync manager
    this.initializeSyncManager();
  }

  // Initialize background sync manager
  initializeSyncManager() {
    // Check for online status and sync when available
    window.addEventListener('online', () => {
      this.syncOfflineQueue();
    });

    // Periodic sync attempt every 30 seconds when online
    setInterval(() => {
      if (navigator.onLine) {
        this.syncOfflineQueue();
      }
    }, 30000);
  }

  // Generate cache key from URL and parameters
  generateCacheKey(url, method = 'GET', data = null) {
    const baseKey = `${method}:${url}`;
    if (data && (method === 'POST' || method === 'PUT')) {
      const dataHash = btoa(JSON.stringify(data)).slice(0, 8);
      return `${baseKey}:${dataHash}`;
    }
    return baseKey;
  }

  // Enhanced cache expiration logic for better offline-first experience
  getCacheExpiration(url, isEmergencyMode = false) {
    // Emergency mode provides very long expiration for critical offline access
    if (isEmergencyMode) {
      return this.cacheExpirations.emergency;
    }

    // URL pattern matching with specific cache strategies
    if (url.includes('/categories') || url.includes('/auth/produce')) {
      return this.cacheExpirations.staticData;
    }
    if (url.includes('/produce') && !url.includes('/auth/produce')) {
      return this.cacheExpirations.produce;
    }
    if (url.includes('/manage/farm')) {
      return this.cacheExpirations.farmManagement;
    }
    if (url.includes('/orders')) {
      return this.cacheExpirations.orders;
    }
    if (url.includes('/conversations') && !url.includes('/messages')) {
      return this.cacheExpirations.conversations;
    }
    if (url.includes('/messages')) {
      return this.cacheExpirations.messages;
    }
    if (url.includes('/profile')) {
      return this.cacheExpirations.profile;
    }
    if (url.includes('/farmer/wallet')) {
      return this.cacheExpirations.wallet;
    }
    if (url.includes('/farmer/withdrawals')) {
      return this.cacheExpirations.withdrawals;
    }
    if (url.includes('/products')) {
      return this.cacheExpirations.products;
    }
    if (url.includes('/categories')) {
      return this.cacheExpirations.categories;
    }

    return this.cacheExpirations.default;
  }

  // Cache GET response
  async cacheResponse(url, response, method = 'GET', data = null, force = false) {
    try {
      const cacheKey = this.generateCacheKey(url, method, data);

      // Check if current entry is "dirty" (optimistically patched)
      const existing = await this.apiCache.getItem(cacheKey);
      if (existing && existing.__dirty && !force) {
        // console.log(`[Cache] Skipping overwrite of dirty entry: ${cacheKey}`);
        return;
      }

      const expiration = this.getCacheExpiration(url);
      const cacheEntry = {
        data: response,
        timestamp: Date.now(),
        expiration: expiration,
        method: method,
        url: url,
        __dirty: false
      };

      await this.apiCache.setItem(cacheKey, cacheEntry);
      await this.updateCacheMetadata(cacheKey, cacheEntry);
    } catch (error) {
      console.error('[Cache] Failed to cache response:', error);
    }
  }

  // Get cached response
  async getCachedResponse(url, method = 'GET', data = null) {
    try {
      const cacheKey = this.generateCacheKey(url, method, data);
      const cacheEntry = await this.apiCache.getItem(cacheKey);

      if (!cacheEntry) {
        return null;
      }

      // Check if cache is expired
      const now = Date.now();
      if (now - cacheEntry.timestamp > cacheEntry.expiration) {
        // Only remove if we're online and can get fresh data
        if (navigator.onLine) {
          await this.apiCache.removeItem(cacheKey);
          return null;
        }
        // In offline mode, return expired data as fallback
      }

      return cacheEntry.data;
    } catch (error) {
      console.error('[Cache] Failed to get cached response:', error);
      return null;
    }
  }

  // Optimistically patch a cached GET response
  async patchCachedResponse(url, patchFn) {
    try {
      const cacheKey = this.generateCacheKey(url, 'GET');
      const cacheEntry = await this.apiCache.getItem(cacheKey);

      if (cacheEntry && cacheEntry.data) {
        // Data is usually { status: true, data: { ... } }
        const wrappedData = cacheEntry.data;
        let originalData = wrappedData.data || wrappedData;

        const patchedData = patchFn(originalData);

        if (wrappedData.data) {
          wrappedData.data = patchedData;
        } else {
          cacheEntry.data = patchedData;
        }

        // Mark as dirty so it's not overwritten by stale network data
        cacheEntry.__dirty = true;
        cacheEntry.timestamp = Date.now(); // Reset timestamp to prevent expiration

        await this.apiCache.setItem(cacheKey, cacheEntry);
        return true;
      }
      return false;
    } catch (error) {
      console.error('[Cache] Failed to patch cached response:', error);
      return false;
    }
  }

  // Add operation to offline queue
  async addToOfflineQueue(operation) {
    try {
      const queueItems = await this.getOfflineQueue();
      const newOperation = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        ...operation,
        timestamp: Date.now(),
        retryCount: 0
      };

      queueItems.push(newOperation);
      await this.offlineQueue.setItem('operations', queueItems);

      return newOperation.id;
    } catch (error) {
      console.error('[Cache] Failed to add to offline queue:', error);
      return null;
    }
  }

  // Get offline queue
  async getOfflineQueue() {
    try {
      const queue = await this.offlineQueue.getItem('operations');
      return queue || [];
    } catch (error) {
      console.error('[Cache] Failed to get offline queue:', error);
      return [];
    }
  }

  // Sync offline queue when online
  async syncOfflineQueue() {
    if (!navigator.onLine || this.syncInProgress) {
      return;
    }

    this.syncInProgress = true;

    try {
      const queueItems = await this.getOfflineQueue();
      if (queueItems.length === 0) {
        this.syncInProgress = false;
        return;
      }

      const remainingItems = [];
      let syncCount = 0;

      for (const operation of queueItems) {
        try {
          const result = await this.executeOfflineOperation(operation);

          if (result.success) {
            syncCount++;

            // For POST/PUT/DELETE operations, invalidate related cache
            if (['POST', 'PUT', 'DELETE'].includes(operation.method)) {
              await this.invalidateRelatedCache(operation.url);
            }
          } else {
            console.warn(`[Sync] Operation ${operation.id} failed, will retry:`, result.error);
            operation.retryCount++;
            if (operation.retryCount < 5) {
              remainingItems.push(operation);
            }
          }
        } catch (error) {
          console.error(`[Sync] Critical error during operation ${operation.id}:`, error);
          operation.retryCount++;
          if (operation.retryCount < 5) {
            remainingItems.push(operation);
          }
        }
      }

      // Update queue with remaining items
      await this.offlineQueue.setItem('operations', remainingItems);

      // If we synced anything, notify the app to refresh data
      if (syncCount > 0) {
        window.dispatchEvent(new CustomEvent('sync-complete', {
          detail: { count: syncCount, timestamp: Date.now() }
        }));
      }

    } catch (error) {
      console.error('[Sync] Failed to sync offline queue:', error);
    } finally {
      this.syncInProgress = false;
    }
  }

  // Execute offline operation
  async executeOfflineOperation(operation) {
    // This will be called by the API service
    // Return structure: { success: boolean, data?: any, error?: string }
    return { success: false, error: 'Not implemented' };
  }

  // Invalidate related cache entries
  async invalidateRelatedCache(url) {
    try {
      const metadata = await this.getCacheMetadata();
      const keysToInvalidate = [];

      // Find related cache entries
      for (const [key, entry] of Object.entries(metadata)) {
        if (this.isRelatedUrl(url, entry.url)) {
          keysToInvalidate.push(key);
        }
      }

      // Remove invalidated entries
      for (const key of keysToInvalidate) {
        await this.apiCache.removeItem(key);
        delete metadata[key];
      }

      await this.metadataStore.setItem('cache_metadata', metadata);

    } catch (error) {
      console.error('[Cache] Failed to invalidate related cache:', error);
    }
  }

  // Force invalidate specific cache entries (for immediate refresh)
  async forceInvalidateCache(urlPatterns = []) {
    try {
      const metadata = await this.getCacheMetadata();
      const keysToInvalidate = [];

      if (urlPatterns.length === 0) {
        // If no patterns provided, clear all cache
        await this.clearAllCache();
        return;
      }

      // Find cache entries matching any of the patterns
      for (const [key, entry] of Object.entries(metadata)) {
        const shouldInvalidate = urlPatterns.some(pattern => {
          if (typeof pattern === 'string') {
            return entry.url.includes(pattern);
          }
          return pattern.test(entry.url);
        });

        if (shouldInvalidate) {
          keysToInvalidate.push(key);
        }
      }

      // Remove invalidated entries
      for (const key of keysToInvalidate) {
        await this.apiCache.removeItem(key);
        delete metadata[key];
      }

      await this.metadataStore.setItem('cache_metadata', metadata);

    } catch (error) {
      console.error('[Cache] Failed to force invalidate cache:', error);
    }
  }

  // Check if URLs are related (for cache invalidation)
  isRelatedUrl(operationUrl, cachedUrl) {
    // Extract base paths
    const getBasePath = (url) => {
      const path = url.split('?')[0]; // Remove query params
      return path.replace(/\/\d+$/, ''); // Remove ID from end
    };

    const opBase = getBasePath(operationUrl);
    const cacheBase = getBasePath(cachedUrl);

    // Same base path or related endpoints
    if (opBase === cacheBase) return true;

    // Specific relationships
    const relations = {
      '/produce': ['/my_produce', '/manage/farm'],
      '/manage/farm': ['/my_produce', '/produce'],
      '/orders': ['/farmer/orders'],
      '/farmer/orders': ['/orders'],
      '/conversations': ['/send-message'],
      '/send-message': ['/conversations'],
      '/farmer/withdraw': ['/farmer/withdrawals', '/farmer/wallet'],
      '/farmer/withdrawals': ['/farmer/wallet'],
      '/farmer/wallet': ['/farmer/withdrawals']
    };

    for (const [base, related] of Object.entries(relations)) {
      if (opBase.includes(base)) {
        return related.some(rel => cacheBase.includes(rel));
      }
    }

    return false;
  }

  // Update cache metadata
  async updateCacheMetadata(cacheKey, cacheEntry) {
    try {
      const metadata = await this.getCacheMetadata();
      metadata[cacheKey] = {
        url: cacheEntry.url,
        method: cacheEntry.method,
        timestamp: cacheEntry.timestamp,
        expiration: cacheEntry.expiration
      };
      await this.metadataStore.setItem('cache_metadata', metadata);
    } catch (error) {
      console.error('[Cache] Failed to update metadata:', error);
    }
  }

  // Get cache metadata
  async getCacheMetadata() {
    try {
      const metadata = await this.metadataStore.getItem('cache_metadata');
      return metadata || {};
    } catch (error) {
      console.error('[Cache] Failed to get metadata:', error);
      return {};
    }
  }

  // Clear expired cache entries
  async clearExpiredCache() {
    try {
      const metadata = await this.getCacheMetadata();
      const now = Date.now();
      const expiredKeys = [];

      for (const [key, entry] of Object.entries(metadata)) {
        if (now - entry.timestamp > entry.expiration) {
          expiredKeys.push(key);
        }
      }

      for (const key of expiredKeys) {
        await this.apiCache.removeItem(key);
        delete metadata[key];
      }

      if (expiredKeys.length > 0) {
        await this.metadataStore.setItem('cache_metadata', metadata);
      }
    } catch (error) {
      console.error('[Cache] Failed to clear expired cache:', error);
    }
  }

  // Clear all cache
  async clearAllCache() {
    try {
      await this.apiCache.clear();
      await this.metadataStore.clear();
    } catch (error) {
      console.error('[Cache] Failed to clear all cache:', error);
    }
  }

  // Get cache statistics
  async getCacheStats() {
    try {
      const metadata = await this.getCacheMetadata();
      const queueItems = await this.getOfflineQueue();

      const now = Date.now();
      let validEntries = 0;
      let expiredEntries = 0;
      let totalSize = 0;

      for (const entry of Object.values(metadata)) {
        if (now - entry.timestamp > entry.expiration) {
          expiredEntries++;
        } else {
          validEntries++;
        }
      }

      return {
        totalEntries: Object.keys(metadata).length,
        validEntries,
        expiredEntries,
        queuedOperations: queueItems.length,
        isOnline: navigator.onLine
      };
    } catch (error) {
      console.error('[Cache] Failed to get cache stats:', error);
      return {
        totalEntries: 0,
        validEntries: 0,
        expiredEntries: 0,
        queuedOperations: 0,
        isOnline: navigator.onLine
      };
    }
  }

  // Enhanced preload critical data with comprehensive coverage
  async preloadCriticalData(apiService) {
    if (!navigator.onLine) {
      return { success: false, reason: 'offline' };
    }

    try {

      const criticalEndpoints = [
        // Core agricultural data
        { url: '/auth/produce', priority: 1, description: 'Available Produce' },
        { url: '/categories', priority: 1, description: 'Categories' },
        { url: '/my_produce', priority: 1, description: 'User Produce' },
        { url: '/manage/farm', priority: 1, description: 'Farm Management' },

        // Financial and order data
        { url: '/farmer/orders', priority: 2, description: 'Orders' },
        { url: '/farmer/wallet', priority: 2, description: 'Wallet' },
        { url: '/farmer/withdrawals', priority: 3, description: 'Withdrawals' },

        // Communication
        { url: '/conversations', priority: 3, description: 'Conversations' },

        // Products
        { url: '/products', priority: 4, description: 'Products' }
      ];

      const results = {
        total: criticalEndpoints.length,
        successful: 0,
        failed: 0,
        errors: []
      };

      // Sort by priority
      const sortedEndpoints = criticalEndpoints.sort((a, b) => a.priority - b.priority);

      for (const endpoint of sortedEndpoints) {
        try {

          if (apiService && typeof apiService.get === 'function') {
            await apiService.get(endpoint.url);
          } else {
            // Direct fetch if no API service provided
            const response = await fetch(endpoint.url, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
                'Content-Type': 'application/json'
              }
            });

            if (response.ok) {
              const data = await response.json();
              await this.cacheResponse(endpoint.url, data, 'GET');
            }
          }

          results.successful++;

        } catch (error) {
          results.failed++;
          results.errors.push({
            endpoint: endpoint.url,
            description: endpoint.description,
            error: error.message
          });
          console.warn(`[Cache] ❌ Failed to preload ${endpoint.description}:`, error.message);
        }
      }

      return { success: true, results };

    } catch (error) {
      console.error('[Cache] Failed to preload critical data:', error);
      return { success: false, error: error.message };
    }
  }

  // Check data freshness and trigger refresh if needed
  async checkDataFreshness() {
    try {
      const metadata = await this.getCacheMetadata();
      const now = Date.now();
      const staleThreshold = 10 * 60 * 1000; // 10 minutes

      const staleEntries = [];

      for (const [key, entry] of Object.entries(metadata)) {
        const age = now - entry.timestamp;
        if (age > staleThreshold) {
          staleEntries.push({
            key,
            url: entry.url,
            age: Math.round(age / 60000), // age in minutes
            expired: age > entry.expiration
          });
        }
      }

      return {
        totalEntries: Object.keys(metadata).length,
        staleEntries: staleEntries.length,
        staleData: staleEntries
      };

    } catch (error) {
      console.error('[Cache] Failed to check data freshness:', error);
      return { totalEntries: 0, staleEntries: 0, staleData: [] };
    }
  }

  // Get cache size information
  async getCacheSize() {
    try {
      let totalSize = 0;
      const cacheInfo = {};

      const cacheNames = [
        { name: 'AgricverseApp', store: 'apiCache' },
        { name: 'AgricverseApp', store: 'offlineQueue' },
        { name: 'AgricverseApp', store: 'metadata' }
      ];

      for (const { name, store } of cacheNames) {
        const instance = localforage.createInstance({ name, storeName: store });
        const keys = await instance.keys();
        let storeSize = 0;

        for (const key of keys) {
          try {
            const item = await instance.getItem(key);
            if (item) {
              const itemSize = new Blob([JSON.stringify(item)]).size;
              storeSize += itemSize;
            }
          } catch (error) {
            console.warn(`[Cache] Error calculating size for ${key}:`, error);
          }
        }

        cacheInfo[store] = {
          entries: keys.length,
          size: storeSize,
          sizeFormatted: this.formatBytes(storeSize)
        };

        totalSize += storeSize;
      }

      return {
        total: {
          size: totalSize,
          sizeFormatted: this.formatBytes(totalSize)
        },
        stores: cacheInfo
      };

    } catch (error) {
      console.error('[Cache] Failed to calculate cache size:', error);
      return { total: { size: 0, sizeFormatted: '0 B' }, stores: {} };
    }
  }

  // Format bytes for human reading
  formatBytes(bytes) {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
}

// Create singleton instance
const cacheService = new CacheService();

export default cacheService;
